import { FC, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useMissingDataHeader } from '@/context/MissingDataHeaderProvider';
import { usePanel } from '@/context/PanelProvider';
import { MissingDataHeader } from './MissingDataHeader';
import { Resizable } from './Resizable';

export const PAGE_MARGIN_CLASSNAME = 'px-10 py-12';
const NAVBAR_ACCOUNT_UI_DEFAULT_WIDTH = 282;
const NAVBAR_LEGACY_UI_DEFAULT_WIDTH = 380;

export const PagePadding: FC<PropsWithChildren<{ withScroll?: boolean }>> = ({
  children,
  withScroll = true,
}) => {
  const { isSinglePaneView } = usePanel();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // Scroll to the top of the page when the user navigates to a new page
  useEffect(() => {
    if (location.pathname && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [location.pathname, scrollContainerRef]);
  return (
    <div
      className={twJoin(
        'h-full overflow-hidden bg-bgMain pb-12',
        withScroll && 'overflow-y-auto',
      )}
      ref={scrollContainerRef}
    >
      <div className={isSinglePaneView ? undefined : PAGE_MARGIN_CLASSNAME}>
        {children}
      </div>
    </div>
  );
};

type PageLayoutParams = {
  header?: () => ReactNode;
  /** `children` is the navbar component on the left-hand aside */
  children: ReactNode;
  showBanner?: boolean;
  className?: string;
  isLoading?: boolean;
};

export const PageLayout: FC<PageLayoutParams> = ({
  header,
  /** children is the navbar on the lefthand side */
  children: sideNavigation,
  showBanner = true,
  className,
  isLoading = false,
}) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { isSinglePaneView } = usePanel();
  const location = useLocation();
  const match = useMatch(
    '/:prefix?/:exploreId/projects/:projectId/environments/:environmentId',
  );

  const { missingSourceFreshness, missingTestResults, missingCatalogInfo } =
    useMissingDataHeader();

  const isExactRoute = match?.pathname === location.pathname;

  if (isSinglePaneView) {
    return isExactRoute ? (
      sideNavigation
    ) : (
      <div data-testid="page-layout-outlet">
        {showBanner && (
          <MissingDataHeader
            className="flex-grow-0"
            missingSourceFreshness={missingSourceFreshness}
            missingTestResults={missingTestResults}
            missingCatalogInfo={missingCatalogInfo}
          />
        )}
        <Outlet />
      </div>
    );
  }

  const width = hasAccountFeature('explorerAccountUI')
    ? NAVBAR_ACCOUNT_UI_DEFAULT_WIDTH
    : NAVBAR_LEGACY_UI_DEFAULT_WIDTH;

  return (
    <>
      {header && header()}
      <div className={twJoin('flex h-full w-full', isLoading && 'hidden')}>
        {sideNavigation && (
          <Resizable
            defaultWidth={width}
            minWidth={width}
            id="side-navigation-container"
            direction="right"
            className={twJoin(
              'h-full flex-none overflow-x-hidden bg-bgMain p-4 md:p-10',
              className,
            )}
          >
            {sideNavigation}
          </Resizable>
        )}
        <span className="relative h-full flex-1 flex-wrap overflow-y-hidden">
          <span className="flex h-full w-full flex-col">
            {showBanner && (
              <MissingDataHeader
                className="flex-grow-0"
                missingSourceFreshness={missingSourceFreshness}
                missingTestResults={missingTestResults}
                missingCatalogInfo={missingCatalogInfo}
              />
            )}
            <span className="min-h-0 flex-grow">
              <Outlet />
            </span>
          </span>
        </span>
      </div>
    </>
  );
};
